import type { FhirReadError } from './read-error';

/**
 * The states an access-gated section of a page can be in. The point of the
 * model is the forbidden/empty split: a compartment-scoped member must see
 * "restricted", not "no data", when the AccessPolicy denies a section —
 * and one denied section must never take down its siblings.
 */
export type FhirSectionStatus =
  | 'loading'
  | 'data'
  | 'empty'
  | 'forbidden'
  | 'error';

const defaultIsEmpty = (data: unknown): boolean =>
  data == null || (Array.isArray(data) && data.length === 0);

/**
 * Collapse a query-state (useFhirData / useFhirConnection / useFhirRestRead /
 * useFhirSearch all fit structurally) into the section status SectionBoundary
 * renders.
 *
 * Precedence, deliberately:
 * - Usable data always wins. A Medplum per-field denial arrives as HTTP 200
 *   with partial data plus a Forbidden error entry — when the section still
 *   mapped to something showable, show it (withheld fields are the
 *   renderer's field-state concern). Stale cache during a refetch also
 *   renders as data, matching cache-and-network semantics.
 * - A denial with nothing showable is 'forbidden' — the root selection came
 *   back null, so the mapped result is empty and only the error tells why.
 * - 'empty' is the residual: the read succeeded and there is nothing there.
 */
export function toSectionStatus<T>(
  state: { loading: boolean; data: T | null; readError: FhirReadError | null },
  isEmpty: (data: T) => boolean = defaultIsEmpty,
): FhirSectionStatus {
  if (state.data !== null && !isEmpty(state.data)) return 'data';
  if (state.loading) return 'loading';
  if (state.readError?.kind === 'forbidden') return 'forbidden';
  if (state.readError) return 'error';
  return 'empty';
}
